// src/pages/registro/RegistroNegocio.js
//
// Formulario de registro de negocio (tendero) contra el backend real.
// Incluye datos del representante legal, categoría de negocio, horario
// estructurado, ubicación GPS y aceptaciones legales.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Stack,
  Alert, Divider, Grid, IconButton, InputAdornment, MenuItem,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import Logo from '../../components/Logo';
import TelefonoInput from '../../components/TelefonoInput';
import SelectorMunicipio from '../../components/registro/SelectorMunicipio';
import CampoUbicacionGPS from '../../components/registro/CampoUbicacionGPS';
import AceptacionLegal from '../../components/registro/AceptacionLegal';
import { registro, catalogos } from '../../api/registroApi';

const CODIGOS_LEGALES = ['TC_NEGOCIO', 'POLITICA_DATOS', 'AUTORIZACION_DATOS'];
const TIPOS_DOC = [
  { v: 'CC',  label: 'Cédula de ciudadanía' },
  { v: 'CE',  label: 'Cédula de extranjería' },
  { v: 'PA',  label: 'Pasaporte' },
  { v: 'NIT', label: 'NIT' },
];
const DIAS = [
  { k: 'L', label: 'Lun' },
  { k: 'M', label: 'Mar' },
  { k: 'X', label: 'Mié' },
  { k: 'J', label: 'Jue' },
  { k: 'V', label: 'Vie' },
  { k: 'S', label: 'Sáb' },
  { k: 'D', label: 'Dom' },
];

export default function RegistroNegocio() {
  const navigate = useNavigate();

  const [f, setF] = useState({
    // Negocio
    nombre: '',
    categoriaNegocioId: '',
    // Representante legal
    nombreRep: '', apellidoRep: '',
    tipoDocRep: 'CC', numeroDocRep: '',
    // Contacto
    telIndicativo: '57', telNumero: '', telefono: '', telefonoValido: false,
    correo: '',
    password: '', mostrarPass: false,
    // Ubicación
    ubicacion: '', detalleEntrega: '', barrio: '',
    municipioId: '',
    latitud: null, longitud: null,
    // Horario
    diasAtencion: '1111110', // Lun-Sáb por default
    horaApertura: '06:30',
    horaCierre:   '20:00',
    color: '#0F4C3A',
  });

  const [categorias, setCategorias] = useState([]);
  const [aceptaciones, setAceptaciones] = useState({});
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [exito, setExito] = useState(false);

  const upd = (k) => (v) => setF(s => ({ ...s, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    (async () => {
      try {
        const r = await catalogos.categoriasNegocio();
        setCategorias(Array.isArray(r) ? r : (r.items || []));
      } catch { /* ignore, se muestra vacío */ }
    })();
  }, []);

  const cambiarAceptacion = ({ codigo, aceptado, checksumAceptado }) => {
    setAceptaciones(s => ({ ...s, [codigo]: { aceptado, checksumAceptado } }));
  };

  const toggleDia = (i) => {
    const arr = f.diasAtencion.split('');
    arr[i] = arr[i] === '1' ? '0' : '1';
    setF(s => ({ ...s, diasAtencion: arr.join('') }));
  };

  const validar = () => {
    if (!f.nombre.trim())         return 'Escribe el nombre del negocio.';
    if (!f.categoriaNegocioId)    return 'Selecciona la categoría del negocio.';
    if (!f.nombreRep.trim())      return 'Escribe el nombre del representante.';
    if (!f.apellidoRep.trim())    return 'Escribe el apellido del representante.';
    if (!f.numeroDocRep.trim())   return 'Escribe el número de documento del representante.';
    if (!f.telefonoValido)        return 'Teléfono inválido.';
    if (!f.correo.includes('@'))  return 'Correo inválido.';
    if ((f.password || '').length < 6) return 'La contraseña debe tener al menos 6 caracteres.';
    if (!f.ubicacion.trim())      return 'Escribe la dirección del negocio.';
    if (!f.municipioId)           return 'Selecciona el municipio.';
    if (!/^[01]{7}$/.test(f.diasAtencion)) return 'Selecciona los días de atención.';
    if (f.diasAtencion === '0000000')      return 'El negocio debe atender al menos un día.';
    for (const c of CODIGOS_LEGALES) {
      if (!aceptaciones[c]?.aceptado) return 'Debes aceptar todos los documentos legales.';
    }
    return null;
  };

  const enviar = async () => {
    const err = validar();
    if (err) { setError(err); return; }
    setError('');
    setCargando(true);

    const catId = parseInt(f.categoriaNegocioId, 10);

    const payload = {
      nombre:                       f.nombre.trim(),
      nombreRepresentante:          f.nombreRep.trim(),
      apellidoRepresentante:        f.apellidoRep.trim(),
      tipoDocumentoRepresentante:   f.tipoDocRep,
      numeroDocumentoRepresentante: f.numeroDocRep.trim(),
      categoriaNegocioId:           catId,
      ubicacion:                    f.ubicacion.trim(),
      detalleEntrega:               f.detalleEntrega?.trim() || null,
      barrio:                       f.barrio?.trim() || null,
      municipioID:                  parseInt(f.municipioId, 10),
      telefono:                     f.telefono,
      correo:                       f.correo.trim(),
      password:                     f.password,
      diasAtencion:                 f.diasAtencion,
      horaApertura:                 f.horaApertura,
      horaCierre:                   f.horaCierre,
      color:                        f.color,
      latitud:                      f.latitud,
      longitud:                     f.longitud,
      aceptaciones: CODIGOS_LEGALES.map(c => ({
        codigo: c,
        checksumAceptado: aceptaciones[c].checksumAceptado,
      })),
    };

    try {
      await registro.registrarNegocio(payload);
      setExito(true);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo completar el registro.');
    } finally { setCargando(false); }
  };

  if (exito) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#FBF7F0', display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', py: 6 }}>
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 72, mb: 2 }} />
              <Typography variant="h4" sx={{ fontFamily: 'Fraunces', mb: 1 }}>Tu negocio quedó registrado</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Nuestro equipo revisará el registro y te avisará cuando esté aprobado. Mientras tanto puedes iniciar sesión
                para completar tu catálogo.
              </Typography>
              <Button variant="contained" size="large" onClick={() => navigate('/login')}>
                Ir a iniciar sesión
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FBF7F0', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/registro')}><ArrowBackIcon /></IconButton>
        <Logo />
      </Container>

      <Container maxWidth="md" sx={{ flexGrow: 1, py: 3 }}>
        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="overline" color="text.secondary">Crear cuenta</Typography>
            <Typography variant="h4" sx={{ fontFamily: 'Fraunces', mb: 1 }}>Registra tu tienda</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Suscripción fija mensual. Sin comisiones por pedido. Sin sorpresas.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Stack spacing={3}>

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>El negocio</Typography>
                <Stack spacing={2}>
                  <TextField fullWidth label="Nombre comercial" value={f.nombre} onChange={upd('nombre')} required />
                  <TextField
                    select fullWidth label="Categoría" value={f.categoriaNegocioId}
                    onChange={upd('categoriaNegocioId')} required
                  >
                    {categorias.map((c) => {
                      const id = c.categoriaNegocioId || c.CategoriaNegocioId || c.categoriaNegocioID;
                      return <MenuItem key={id} value={id}>{c.nombre || c.Nombre}</MenuItem>;
                    })}
                  </TextField>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Representante legal</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  La persona a cargo del negocio ante HolaVeci (dueño o administrador).
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Nombre(s)" value={f.nombreRep} onChange={upd('nombreRep')} required />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Apellido(s)" value={f.apellidoRep} onChange={upd('apellidoRep')} required />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      select fullWidth label="Tipo de documento" value={f.tipoDocRep}
                      onChange={upd('tipoDocRep')}
                    >
                      {TIPOS_DOC.map(t => <MenuItem key={t.v} value={t.v}>{t.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <TextField fullWidth label="Número de documento" value={f.numeroDocRep} onChange={upd('numeroDocRep')} required />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Datos de contacto</Typography>
                <Stack spacing={2}>
                  <TelefonoInput
                    indicativo={f.telIndicativo}
                    numero={f.telNumero}
                    onChange={(ind, num, e164, valido) => setF(s => ({
                      ...s,
                      telIndicativo: ind, telNumero: num,
                      telefono: e164, telefonoValido: valido,
                    }))}
                  />
                  <TextField fullWidth label="Correo" type="email" value={f.correo} onChange={upd('correo')} required />
                  <TextField
                    fullWidth label="Contraseña"
                    type={f.mostrarPass ? 'text' : 'password'}
                    value={f.password}
                    onChange={upd('password')}
                    helperText="Mínimo 6 caracteres."
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setF(s => ({ ...s, mostrarPass: !s.mostrarPass }))} edge="end">
                            {f.mostrarPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Ubicación del negocio</Typography>
                <Stack spacing={2}>
                  <SelectorMunicipio value={f.municipioId} onChange={upd('municipioId')} />
                  <TextField
                    fullWidth label="Dirección"
                    placeholder="Ej: Calle 45 #12-34"
                    value={f.ubicacion}
                    onChange={upd('ubicacion')}
                    required
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth label="Detalle (local, torre)"
                        placeholder="Ej: Local 5, esquina"
                        value={f.detalleEntrega}
                        onChange={upd('detalleEntrega')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth label="Barrio o conjunto (opcional)"
                        value={f.barrio}
                        onChange={upd('barrio')}
                      />
                    </Grid>
                  </Grid>
                  <CampoUbicacionGPS
                    lat={f.latitud} lng={f.longitud}
                    onChange={({ lat, lng }) => setF(s => ({ ...s, latitud: lat, longitud: lng }))}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Horario de atención</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Días</Typography>
                    <ToggleButtonGroup size="small">
                      {DIAS.map((d, i) => (
                        <ToggleButton
                          key={d.k}
                          value={d.k}
                          selected={f.diasAtencion[i] === '1'}
                          onChange={() => toggleDia(i)}
                        >
                          {d.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth type="time" label="Apertura"
                        value={f.horaApertura} onChange={upd('horaApertura')}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth type="time" label="Cierre"
                        value={f.horaCierre} onChange={upd('horaCierre')}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    fullWidth type="color" label="Color distintivo (opcional)"
                    value={f.color} onChange={upd('color')}
                    sx={{ maxWidth: 200 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Términos legales</Typography>
                <Stack spacing={0.5}>
                  {CODIGOS_LEGALES.map(c => (
                    <AceptacionLegal
                      key={c}
                      codigo={c}
                      aceptado={!!aceptaciones[c]?.aceptado}
                      onChange={cambiarAceptacion}
                    />
                  ))}
                </Stack>
              </Box>

              <Button
                variant="contained" size="large" onClick={enviar} disabled={cargando}
                sx={{ py: 1.5, fontSize: 16 }}
              >
                {cargando ? 'Creando cuenta…' : 'Registrar mi negocio'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
